import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api, { formatCurrency, formatDate } from '../utils/api';
import { generatePDF } from '../utils/pdfGenerator';
import { useAuth } from '../context/AuthContext';
import toast from 'react-hot-toast';

const STATUS = ['','draft','sent','accepted','rejected','converted'];
const badge = (s) => <span className={`badge badge-${s}`}>{s}</span>;

export default function Quotations() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [rows, setRows]     = useState([]);
  const [loading, setLoad]  = useState(true);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [reverting, setRev] = useState(null);

  const fetch = useCallback(() => {
    setLoad(true);
    api.get('/quotations', { params:{ status:filter, search } })
      .then(r=>{ setRows(r.data.data||[]); setLoad(false); }).catch(()=>setLoad(false));
  }, [filter, search]);

  useEffect(() => { const t=setTimeout(fetch,300); return ()=>clearTimeout(t); }, [fetch]);

  const del = async (id) => {
    if (!window.confirm('Delete this quotation?')) return;
    await api.delete(`/quotations/${id}`); toast.success('Deleted'); fetch();
  };

  const convert = async (id, qNum) => {
    if (!window.confirm(`Convert ${qNum} to an invoice?`)) return;
    const res = await api.post(`/quotations/${id}/convert`);
    const msg = res.data.renewal_created ? `Invoice ${res.data.invoice_number} + Renewal created automatically!` : `Invoice ${res.data.invoice_number} created!`;
    toast.success(msg); nav('/invoices');
  };

  const changeStatus = async (id, status) => {
    const revert = (status === 'sent' || status === 'accepted') && reverting === id;
    await api.put(`/quotations/${id}/status`, { status, revert_invoice: revert });
    toast.success('Status updated'); setRev(null); fetch();
  };

  const dlPdf = async (id) => {
    const res = await api.get(`/quotations/${id}`);
    await generatePDF(res.data.data, 'quotation', user);
  };

  const whatsapp = async (id, row) => {
    const res = await api.get(`/quotations/${id}`);
    const q = res.data.data;
    const txt = `Hi ${q.client_name},\n\nPlease find your quotation *${q.quotation_number}* for *${formatCurrency(q.total)}*.\n\nValid until: ${formatDate(q.valid_until)}\n\nThank you,\n${user?.company_name || ''}`;
    window.open(`https://wa.me/${row.client_phone?.replace(/\D/g,'')||''}?text=${encodeURIComponent(txt)}`, '_blank');
  };

  return (
    <div>
      <div className="page-header">
        <div><h2 className="page-title">Quotations</h2><p className="page-subtitle">{rows.length} record{rows.length!==1?'s':''}</p></div>
        <div className="page-actions">
          <div className="search-box"><span>🔍</span><input placeholder="Search…" value={search} onChange={e=>setSearch(e.target.value)} /></div>
          <button className="btn btn-primary" onClick={()=>nav('/quotations/new')}>+ New Quotation</button>
        </div>
      </div>

      <div className="tabs">
        {STATUS.map(s=>(
          <div key={s} className={`tab ${filter===s?'active':''}`} onClick={()=>setFilter(s)}>
            {s?s.charAt(0).toUpperCase()+s.slice(1):'All'}
          </div>
        ))}
      </div>

      <div className="card" style={{ padding:0 }}>
        {loading ? <div style={{ padding:40,textAlign:'center',color:'var(--text3)' }}>Loading…</div>
        : rows.length===0 ? (
          <div className="empty-state"><div className="empty-icon">📋</div><h3>No quotations</h3><p>Create your first quotation to get started</p>
            <button className="btn btn-primary" onClick={()=>nav('/quotations/new')}>+ New Quotation</button></div>
        ) : (
          <div className="table-wrapper">
            <table>
              <thead><tr><th>Number</th><th>Client</th><th>Issue Date</th><th>Valid Until</th><th>Total</th><th>Renewal</th><th>Status</th><th>Actions</th></tr></thead>
              <tbody>
                {rows.map(q=>(
                  <tr key={q.id}>
                    <td style={{ fontWeight:700,color:'var(--accent2)' }}>{q.quotation_number}</td>
                    <td>{q.client_name}</td>
                    <td>{formatDate(q.issue_date)}</td>
                    <td>{formatDate(q.valid_until)}</td>
                    <td style={{ fontWeight:600 }}>{formatCurrency(q.total)}</td>
                    <td>{parseInt(q.renewal_required) ? <span style={{ fontSize:11.5,background:'var(--infoBg)',color:'var(--info)',borderRadius:100,padding:'2px 8px' }}>🔄 {q.renewal_months}m</span> : <span style={{ color:'var(--text3)',fontSize:12 }}>—</span>}</td>
                    <td>
                      <select className="form-control" style={{ padding:'4px 8px',fontSize:12,width:'auto',minWidth:100 }}
                        value={q.status}
                        onChange={e => {
                          if (q.status==='converted' && (e.target.value==='sent'||e.target.value==='accepted')) {
                            if (window.confirm('This will DELETE the linked invoice. Continue?')) {
                              setRev(q.id); changeStatus(q.id, e.target.value);
                            }
                          } else changeStatus(q.id, e.target.value);
                        }}>
                        {STATUS.filter(s=>s).map(s=><option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td>
                      <div style={{ display:'flex',gap:4 }}>
                        <button className="btn btn-sm btn-secondary btn-icon" onClick={()=>nav(`/quotations/${q.id}/edit`)} title="Edit">✏️</button>
                        <button className="btn btn-sm btn-secondary btn-icon" onClick={()=>dlPdf(q.id)} title="PDF">📄</button>
                        <button className="btn btn-sm btn-secondary btn-icon" onClick={()=>whatsapp(q.id,q)} title="WhatsApp">💬</button>
                        {q.status!=='converted' && <button className="btn btn-sm btn-success btn-icon" onClick={()=>convert(q.id,q.quotation_number)} title="Convert to Invoice">🧾</button>}
                        <button className="btn btn-sm btn-danger btn-icon" onClick={()=>del(q.id)} title="Delete">🗑️</button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
